import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  User as FirebaseUser,
  onAuthStateChanged,
  Unsubscribe,
} from "firebase/auth";
import { auth } from "./init";

const googleProvider = new GoogleAuthProvider();

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  isAdmin: boolean;
  isStaff: boolean;
  isDeveloper: boolean;
  isObserver: boolean;
}

/**
 * Google 登入
 */
export async function signInWithGoogle(): Promise<FirebaseUser> {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    // 強制刷新 token 以獲取最新的 Custom Claims
    // 權限檢查現在由服務端（Functions）處理，通過 Custom Claims 和 Firestore Rules
    await user.getIdToken(true);
    
    return user;
  } catch (error) {
    console.error("Error signing in:", error);
    throw error;
  }
}

/**
 * 登出
 */
export async function signOutUser(): Promise<void> {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error signing out:", error);
    throw error;
  }
}

/**
 * 獲取當前用戶
 */
export function getCurrentUser(): FirebaseUser | null {
  return auth.currentUser;
}

/**
 * 監聽認證狀態變化
 */
export function onAuthStateChange(
  callback: (user: FirebaseUser | null) => void
): Unsubscribe {
  return onAuthStateChanged(auth, callback);
}

/**
 * 檢查用戶是否有管理員權限（優先從 Custom Claims 讀取，Firestore 作為備份）
 * @param forceRefresh 是否強制刷新 token（用於獲取最新的 claims）
 */
export async function checkAdminStatus(user: FirebaseUser, forceRefresh: boolean = false): Promise<boolean> {
  try {
    // 優先從 Custom Claims 讀取（快速、無需讀取 Firestore）
    const idTokenResult = await user.getIdTokenResult(forceRefresh);
    const adminClaim = idTokenResult.claims.admin;
    
    // 明確檢查是否為 true（處理 null/undefined/false）
    if (adminClaim === true) {
      return true;
    }
    
    // 如果 Custom Claims 中沒有或為 false，嘗試從 Firestore 讀取（作為備份）
    // 這通常發生在 Custom Claims 尚未同步時
    try {
      const { getIAMUser } = await import("./iam");
      const iamUser = await getIAMUser(user.uid);
      return iamUser?.isAdmin === true;
    } catch (firestoreError) {
      // Firestore 讀取失敗時，使用 Custom Claims 的結果（可能是 false）
      return false;
    }
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
}

/**
 * 檢查用戶是否有工作人員權限（admin、staff 或 developer，優先從 Custom Claims 讀取，Firestore 作為備份）
 * @param forceRefresh 是否強制刷新 token（用於獲取最新的 claims）
 */
export async function checkStaffStatus(user: FirebaseUser, forceRefresh: boolean = false): Promise<boolean> {
  try {
    // 優先從 Custom Claims 讀取（快速、無需讀取 Firestore）
    const idTokenResult = await user.getIdTokenResult(forceRefresh);
    const adminClaim = idTokenResult.claims.admin;
    const staffClaim = idTokenResult.claims.staff;
    const developerClaim = idTokenResult.claims.developer;
    
    // 明確檢查是否為 true（處理 null/undefined/false）
    if (adminClaim === true || staffClaim === true || developerClaim === true) {
      return true;
    }
    
    // 如果 Custom Claims 中沒有或為 false，嘗試從 Firestore 讀取（作為備份）
    try {
      const { getIAMUser } = await import("./iam");
      const iamUser = await getIAMUser(user.uid);
      return iamUser?.isAdmin === true || iamUser?.isStaff === true || iamUser?.isDeveloper === true;
    } catch (firestoreError) {
      // Firestore 讀取失敗時，使用 Custom Claims 的結果（可能是 false）
      return false;
    }
  } catch (error) {
    console.error("Error checking staff status:", error);
    return false;
  }
}

/**
 * 檢查用戶是否有 developer 權限（優先從 Custom Claims 讀取，Firestore 作為備份）
 * @param forceRefresh 是否強制刷新 token（用於獲取最新的 claims）
 */
export async function checkDeveloperStatus(user: FirebaseUser, forceRefresh: boolean = false): Promise<boolean> {
  try {
    // 優先從 Custom Claims 讀取（快速、無需讀取 Firestore）
    const idTokenResult = await user.getIdTokenResult(forceRefresh);
    const developerClaim = idTokenResult.claims.developer;
    
    // 明確檢查是否為 true（處理 null/undefined/false）
    if (developerClaim === true) {
      return true;
    }
    
    // 如果 Custom Claims 中沒有或為 false，嘗試從 Firestore 讀取（作為備份）
    try {
      const { getIAMUser } = await import("./iam");
      const iamUser = await getIAMUser(user.uid);
      return iamUser?.isDeveloper === true;
    } catch (firestoreError) {
      // Firestore 讀取失敗時，使用 Custom Claims 的結果（可能是 false）
      return false;
    }
  } catch (error) {
    console.error("Error checking developer status:", error);
    return false;
  }
}

/**
 * 檢查用戶是否有 observer 權限（優先從 Custom Claims 讀取，Firestore 作為備份）
 * @param forceRefresh 是否強制刷新 token（用於獲取最新的 claims）
 */
export async function checkObserverStatus(user: FirebaseUser, forceRefresh: boolean = false): Promise<boolean> {
  try {
    // 優先從 Custom Claims 讀取（快速、無需讀取 Firestore）
    const idTokenResult = await user.getIdTokenResult(forceRefresh);
    const observerClaim = idTokenResult.claims.observer;
    
    // 明確檢查是否為 true（處理 null/undefined/false）
    if (observerClaim === true) {
      return true;
    }
    
    // 如果 Custom Claims 中沒有或為 false，嘗試從 Firestore 讀取（作為備份）
    try {
      const { getIAMUser } = await import("./iam");
      const iamUser = await getIAMUser(user.uid);
      return iamUser?.isObserver === true;
    } catch (firestoreError) {
      // Firestore 讀取失敗時，使用 Custom Claims 的結果（可能是 false）
      return false;
    }
  } catch (error) {
    console.error("Error checking observer status:", error);
    return false;
  }
}

/**
 * 轉換 Firebase User 為 AuthUser
 * @param forceRefresh 是否強制刷新 token（用於獲取最新的 claims）
 */
export async function convertToAuthUser(user: FirebaseUser, forceRefresh: boolean = false): Promise<AuthUser> {
  const isAdmin = await checkAdminStatus(user, forceRefresh);
  const isStaff = await checkStaffStatus(user, forceRefresh);
  const isDeveloper = await checkDeveloperStatus(user, forceRefresh);
  const isObserver = await checkObserverStatus(user, forceRefresh);
  
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    isAdmin,
    isStaff,
    isDeveloper,
    isObserver,
  };
}

